import { IonButton, IonButtons, IonCol, IonContent, IonDatetime, IonFooter, IonGrid, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonPopover, IonRadio, IonRadioGroup, IonRow, IonTitle, IonToast, IonToolbar } from '@ionic/react';
import { arrowBackOutline, calendarOutline, timeOutline } from 'ionicons/icons';
import React, { useContext, useMemo, useRef, useState } from 'react';
import { MessageContext } from '../../../contexts/MessageContext';
import './Agendamento.css';

type Props = {
  closeAction: Function;
  closeActionSimple: Function;
}

// Mapeia dayOfWeek do backend para número JS (0=domingo, 1=segunda, ...)
const DAY_MAP: Record<string, number> = {
  SUNDAY: 0, MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3,
  THURSDAY: 4, FRIDAY: 5, SATURDAY: 6,
};

const Agendamento: React.FC<Props> = (props: Props) => {

  const { dadosEmpresa } = useContext(MessageContext);

  const [showToast, setShowToast] = useState(false);
  const [textToast, setTextToast] = useState("");

  const [tipoAgendamento, setTipoAgendamento] = useState("");
  const [dataAgendamento, setDataAgendamento] = useState("");

  // Inicializar hora com minutos arredondados para múltiplo de 10
  const getHoraInicialArredondada = () => {
    const now = new Date();
    const minutos = now.getMinutes();
    const minutosArredondados = Math.ceil(minutos / 10) * 10;
    now.setMinutes(minutosArredondados);
    now.setSeconds(0);
    now.setMilliseconds(0);

    // Ajustar para o fuso horário local antes de converter para ISO
    const offsetMs = now.getTimezoneOffset() * 60000;
    const localTime = new Date(now.getTime() - offsetMs);
    return localTime.toISOString();
  };

  const [horaAgendamento, setHoraAgendamento] = useState("");

  // Estados para controlar popovers
  const [showPopoverData, setShowPopoverData] = useState(false);
  const [showPopoverHora, setShowPopoverHora] = useState(false);

  // Estados temporários para edição
  const [tempDataAgendamento, setTempDataAgendamento] = useState("");
  const [tempHoraAgendamento, setTempHoraAgendamento] = useState("");

  const popoverDateRef = useRef<HTMLIonPopoverElement>(null);
  const popoverTimeRef = useRef<HTMLIonPopoverElement>(null);

  // Dias da semana que a empresa funciona
  const diasDisponiveis = useMemo(() => {
    const horarios = dadosEmpresa?.horarios;
    if (!horarios || !Array.isArray(horarios)) return new Set<number>();
    return new Set(horarios.map((h: any) => DAY_MAP[h.dayOfWeek]).filter((d: any) => d !== undefined));
  }, [dadosEmpresa]);

  // Verifica se uma data está habilitada (dia da semana disponível e com horários ainda válidos)
  const isDateEnabled = (dateString: string) => {
    const date = new Date(dateString);
    if (!diasDisponiveis.has(date.getUTCDay())) return false;

    // Se for hoje, verifica se o horário de funcionamento ainda não encerrou
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    const now = new Date();
    const isToday = year === now.getFullYear() && month === (now.getMonth() + 1) && day === now.getDate();

    if (isToday) {
      const horario = getHorarioParaDia(dateString);
      if (!horario) return false;
      const [startH, startM] = horario.start.split(':').map(Number);
      const endTotalMinutes = startH * 60 + startM + horario.duration;
      const endH = Math.floor(endTotalMinutes / 60) % 24;
      const endM = endTotalMinutes % 60;
      const nowTotalMinutes = now.getHours() * 60 + now.getMinutes();
      return nowTotalMinutes < (endH * 60 + endM);
    }

    return true;
  };

  // Retorna horário de funcionamento para o dia selecionado
  const getHorarioParaDia = (isoDate: string) => {
    const horarios = dadosEmpresa?.horarios;
    if (!horarios || !Array.isArray(horarios) || !isoDate) return null;
    const date = new Date(isoDate);
    const dayOfWeekNum = date.getUTCDay();
    const dayName = Object.keys(DAY_MAP).find(k => DAY_MAP[k] === dayOfWeekNum);
    return horarios.find((h: any) => h.dayOfWeek === dayName) || null;
  };

  // Gera min/max de hora baseado no horário de funcionamento do dia selecionado
  const getHoraLimites = (isoDate: string) => {
    const horario = getHorarioParaDia(isoDate);
    if (!horario) return { minHora: undefined, maxHora: undefined, startH: 0, startM: 0, endH: 23, endM: 59 };

    const [startH, startM] = horario.start.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = startMinutes + horario.duration;

    const endH = Math.floor(endMinutes / 60) % 24;
    const endM = endMinutes % 60;

    const pad = (n: number) => String(n).padStart(2, '0');
    return {
      minHora: `${pad(startH)}:${pad(startM)}`,
      maxHora: `${pad(endH)}:${pad(endM)}`,
      startH,
      startM,
      endH,
      endM,
    };
  };

  // Verifica se a data ISO selecionada é hoje (sem conversão de fuso)
  const isHoje = (isoDate: string): boolean => {
    const datePart = isoDate.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    const now = new Date();
    return year === now.getFullYear() && month === (now.getMonth() + 1) && day === now.getDate();
  };

  // Gera os valores de hora disponíveis para o seletor (hourValues do IonDatetime)
  const getHourValues = (isoDate: string): string | undefined => {
    const horario = getHorarioParaDia(isoDate);
    if (!horario) return undefined;

    const [startH, startM] = horario.start.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = startMinutes + horario.duration;
    const endH = Math.floor(endMinutes / 60) % 24;
    const endM = endMinutes % 60;

    let hours: number[] = [];
    for (let h = startH; h <= endH; h++) {
      hours.push(h % 24);
    }

    // Se for hoje, remove horas que já passaram
    if (isHoje(isoDate)) {
      const now = new Date();
      const nowH = now.getHours();
      const nowM = now.getMinutes();
      hours = hours.filter(h => {
        if (h > nowH) return true;
        if (h === nowH) {
          // Mantém a hora atual só se ainda houver minutos futuros disponíveis
          const limiteEndM = (h === endH) ? endM : 50;
          const proximoMinuto = Math.ceil((nowM + 1) / 10) * 10;
          return proximoMinuto <= limiteEndM;
        }
        return false;
      });
    }

    return hours.length > 0 ? hours.join(',') : undefined;
  };

  // Gera os minutos disponíveis de acordo com a hora selecionada e o horário de funcionamento
  const getMinuteValues = (isoDate: string, isoTime: string): string => {
    const horario = getHorarioParaDia(isoDate);
    if (!horario || !isoTime) return "0,10,20,30,40,50";

    const [startH, startM] = horario.start.split(':').map(Number);
    const endTotalMinutes = startH * 60 + startM + horario.duration;
    const endH = Math.floor(endTotalMinutes / 60) % 24;
    const endM = endTotalMinutes % 60;

    let currentHour = startH;
    if (isoTime.includes('T')) {
      currentHour = parseInt(isoTime.split('T')[1].split(':')[0]);
    }

    const allMinutes = [0, 10, 20, 30, 40, 50];
    let valid = allMinutes;

    if (currentHour === startH && currentHour === endH) {
      valid = allMinutes.filter(m => m >= startM && m <= endM);
    } else if (currentHour === startH) {
      valid = allMinutes.filter(m => m >= startM);
    } else if (currentHour === endH) {
      valid = allMinutes.filter(m => m <= endM);
    }

    // Se for hoje e a hora selecionada for a atual, remove minutos que já passaram
    if (isHoje(isoDate)) {
      const now = new Date();
      if (currentHour === now.getHours()) {
        valid = valid.filter(m => m > now.getMinutes());
      }
    }

    return valid.length > 0 ? valid.join(',') : "0";
  };

  // Função para formatar a data no padrão brasileiro
  function formatarData(isoDate: string): string {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const ano = date.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  // Função para formatar a hora
  function formatarHora(isoTime: string): string {
    if (!isoTime) return "";
    // Extrair a parte da hora do formato ISO (2025-12-01T18:10)
    const timePart = isoTime.includes('T') ? isoTime.split('T')[1] : isoTime;
    const [hora, minuto] = timePart.split(':');
    return `${hora}:${minuto}`;
  }

  return (
    <>

      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={() => props.closeActionSimple()}>
              <IonIcon icon={arrowBackOutline} />
            </IonButton>
          </IonButtons>
          <IonTitle>Data do Pedido</IonTitle>

        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen={false}>

        <div className="agendamento-titulo1">
          Quando deseja receber seu pedido?
        </div>

        <IonRadioGroup value={tipoAgendamento} onIonChange={(e: any) => setTipoAgendamento(e.detail.value)}>
          <IonItem lines="none">
            <IonLabel>Agora</IonLabel>
            <IonRadio slot="end" value="AGORA" />
          </IonItem>

          <IonItem lines="none">
            <IonLabel>Agendar pedido</IonLabel>
            <IonRadio slot="end" value="AGENDADO" />
          </IonItem>
        </IonRadioGroup>

        {tipoAgendamento === "AGENDADO" && (
          <>
            <div className="agendamento-titulo2">
              Selecione a <span className="agendamento-whatsapp-text">data e hora</span> para o agendamento do seu pedido
            </div>

            <IonItem
              lines="none"
              className="input-item"
              button
              onClick={(e) => {
                popoverDateRef.current!.event = e;
                setTempDataAgendamento(dataAgendamento); // Inicializa estado temporário
                setShowPopoverData(true);
              }}
            >
              <IonIcon slot="start" icon={calendarOutline} />
              <IonInput
                value={dataAgendamento ? formatarData(dataAgendamento) : ""}
                placeholder="Selecione a data"
                readonly
              ></IonInput>
            </IonItem>

            <IonPopover
              ref={popoverDateRef}
              isOpen={showPopoverData}
              onDidDismiss={() => setShowPopoverData(false)}
            >
              <IonContent>
                <IonDatetime
                  presentation="date"
                  locale="pt-BR"
                  value={tempDataAgendamento}
                  isDateEnabled={isDateEnabled}
                  onIonChange={(e: any) => {
                    if (e.detail.value) {
                      setTempDataAgendamento(e.detail.value);
                    }
                  }}
                  min={new Date(new Date().getTime() - (new Date().getTimezoneOffset() * 60000)).toISOString()}
                  max={new Date(new Date().getTime() - (new Date().getTimezoneOffset() * 60000) + (365 * 24 * 60 * 60 * 1000)).toISOString()}
                />
              </IonContent>

              <IonFooter className="ion-no-border">
                <IonToolbar>
                  <IonButtons slot="end">
                    <IonButton onClick={() => {
                      setTempDataAgendamento(""); // Limpa temporário
                      setShowPopoverData(false);
                    }}>
                      Cancelar
                    </IonButton>
                  </IonButtons>
                  <IonButtons slot="end">
                    <IonButton strong onClick={() => {
                      if (tempDataAgendamento !== dataAgendamento) {
                        setHoraAgendamento(""); // Reseta hora ao trocar de data
                      }
                      setDataAgendamento(tempDataAgendamento); // Confirma mudança
                      setShowPopoverData(false);
                    }}>
                      Confirmar
                    </IonButton>
                  </IonButtons>
                </IonToolbar>
              </IonFooter>
            </IonPopover>

            <IonItem
              lines="none"
              className="input-item"
              button
              onClick={(e) => {
                popoverTimeRef.current!.event = e;
                // Se já tiver hora, usa ela. Se não, usa a hora sugerida (arredondada)
                setTempHoraAgendamento(horaAgendamento || getHoraInicialArredondada());
                setShowPopoverHora(true);
              }}
            >
              <IonIcon slot="start" icon={timeOutline} />
              <IonInput
                value={horaAgendamento ? formatarHora(horaAgendamento) : ""}
                placeholder="Selecione a hora"
                readonly
              />
            </IonItem>

            <IonPopover
              ref={popoverTimeRef}
              isOpen={showPopoverHora}
              onDidDismiss={() => setShowPopoverHora(false)}
            >
              <IonContent>
                <IonDatetime
                  presentation="time"
                  locale="pt-BR"
                  minuteValues={getMinuteValues(dataAgendamento, tempHoraAgendamento)}
                  hourValues={dataAgendamento ? getHourValues(dataAgendamento) : undefined}
                  value={tempHoraAgendamento}
                  onIonChange={(e: any) => {
                    if (e.detail.value) {
                      setTempHoraAgendamento(e.detail.value);
                    }
                  }}
                />
              </IonContent>

              <IonFooter className="ion-no-border">
                <IonToolbar>
                  <IonButtons slot="end">
                    <IonButton onClick={() => {
                      setTempHoraAgendamento(""); // Limpa temporário
                      setShowPopoverHora(false);
                    }}>
                      Cancelar
                    </IonButton>
                  </IonButtons>
                  <IonButtons slot="end">
                    <IonButton strong onClick={() => {
                      setHoraAgendamento(tempHoraAgendamento); // Confirma mudança
                      setShowPopoverHora(false);
                    }}>
                      Confirmar
                    </IonButton>
                  </IonButtons>
                </IonToolbar>
              </IonFooter>
            </IonPopover>
          </>
        )}

        <IonToast
          isOpen={showToast}
          position="middle"
          onDidDismiss={() => setShowToast(false)}
          message={textToast}
          duration={3000}
        />

      </IonContent>

      <IonFooter className="ion-no-border">
        <IonToolbar>
          <IonGrid>
            <IonRow>
              <IonCol>
                <IonButton
                  style={{ height: "46px", fontSize: "18px", backgroundColor: "#4CAF50", color: "#fff" }}
                  onClick={async () => {

                    if (!tipoAgendamento) {
                      setTextToast("Selecione uma opção para continuar.");
                      setShowToast(true);
                      return;
                    }

                    if (tipoAgendamento === "AGENDADO") {
                      if (!dataAgendamento) {
                        setTextToast("Selecione a data de entrega.");
                        setShowToast(true);
                        return;
                      }

                      if (!horaAgendamento) {
                        setTextToast("Selecione a hora de entrega.");
                        setShowToast(true);
                        return;
                      }

                      // Validar se a data/hora não é no passado

                      // Criar objeto Date a partir da string ISO da data (converte UTC para Local automaticamente)
                      const dataObj = new Date(dataAgendamento);
                      const ano = dataObj.getFullYear();
                      const mes = dataObj.getMonth(); // 0-11
                      const dia = dataObj.getDate();

                      // Extrair HH:mm da hora selecionada
                      let hora = 0;
                      let minuto = 0;

                      if (horaAgendamento.includes('T')) {
                        // Extrair HH:mm diretamente da string ISO para evitar conversão de fuso horário
                        // O IonDatetime retorna algo como "2025-12-03T22:30:00" (às vezes com Z, às vezes sem)
                        // Queremos apenas os números visuais
                        const timePart = horaAgendamento.split('T')[1];
                        const [h, m] = timePart.split(':');
                        hora = parseInt(h);
                        minuto = parseInt(m);
                      } else {
                        // Se for apenas HH:mm
                        const [h, m] = horaAgendamento.split(':').map(Number);
                        hora = h;
                        minuto = m;
                      }

                      // Criar data final combinada no fuso local
                      const dataHoraSelecionada = new Date(ano, mes, dia, hora, minuto);

                      const agora = new Date();
                      // Zerar segundos para comparação justa
                      agora.setSeconds(0);
                      agora.setMilliseconds(0);

                      console.log("Data String:", dataAgendamento);
                      console.log("Data Local Interpretada:", dataHoraSelecionada);
                      console.log("Agora:", agora);

                      if (dataHoraSelecionada < agora) {
                        setTextToast("A data de agendamento deve ser futura.");
                        setShowToast(true);
                        return;
                      }

                      // Validar se o horário está dentro do funcionamento da empresa
                      const limites = getHoraLimites(dataAgendamento);
                      if (limites.minHora && limites.maxHora) {
                        const horaStr = `${String(hora).padStart(2, '0')}:${String(minuto).padStart(2, '0')}`;
                        if (horaStr < limites.minHora || horaStr > limites.maxHora) {
                          setTextToast(`Horário fora do funcionamento. Disponível das ${limites.minHora} às ${limites.maxHora}.`);
                          setShowToast(true);
                          return;
                        }
                      }

                      props.closeAction(dataAgendamento, horaAgendamento, tipoAgendamento);
                    } else {
                      // Tipo AGORA
                      props.closeAction("", "", tipoAgendamento);
                    }

                  }}
                  expand="block"
                  type="submit"
                >
                  Confirmar
                </IonButton>

              </IonCol>
            </IonRow>
          </IonGrid>
        </IonToolbar>
      </IonFooter>


    </>
  );
};

export default ({ closeAction, closeActionSimple }: { closeAction: Function, closeActionSimple: Function }) => (
  <Agendamento closeAction={closeAction} closeActionSimple={closeActionSimple}>
  </Agendamento>
)
